import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../context/AuthContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import LanguageSwitcher from "../common/LanguageSwitcher";
import UMChangePasswordModal from "../../features/university-manager/UMChangePasswordModal";

const UniversityManagerHeader = ({ sidebarCollapsed }) => {
  const location = useLocation();
  const { t } = useTranslation(["universityManager", "navigation", "common"]);
  const { logout: mainLogout } = useAuth();
  const uniAdminAuth = useUniAdminAuth();
  useTenant();

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    if (location.pathname.startsWith("/uni-admin") && uniAdminAuth?.logout) {
      uniAdminAuth.logout();
    } else if (uniAdminAuth?.logout) {
      uniAdminAuth.logout();
    } else if (mainLogout) {
      mainLogout();
    } else {
      window.location.href = "/uni-admin/login";
    }
  };

  const routeTitleKeys = {
    "/university-manager": "dashboard",
    "/university-manager/dashboard": "dashboard",
    "/university-manager/university-info": "universityInfo",
    "/university-manager/campuses": "campuses",
    "/university-manager/faculties": "faculties",
    "/university-manager/departments": "departments",
    "/university-manager/events/awaiting": "awaitingApproval",
    "/university-manager/events/all": "allEvents",
    "/university-manager/clubs": "clubs",
    "/university-manager/users": "users",
    "/university-manager/authority-management": "authorityManagement",
    "/university-manager/finance/transfers": "financeTransfers",
    "/university-manager/log-records": "logRecords",
    // Real /uni-admin tree uses different leaf paths than the demo tree for
    // these two sections (finance vs finance/transfers, audit-log vs log-records).
    "/university-manager/finance": "financeTransfers",
    "/university-manager/audit-log": "logRecords",
  };

  const normalizedPath = location.pathname.startsWith("/uni-admin")
    ? location.pathname.replace("/uni-admin", "/university-manager")
    : location.pathname;

  const titleKey = normalizedPath.startsWith("/university-manager/clubs/")
    ? "clubDetail"
    : routeTitleKeys[normalizedPath] || "dashboard";
  const pageTitle = t(`header.pageTitles.${titleKey}`);

  const userEmail = uniAdminAuth?.user?.email;
  const isSuperAdmin = uniAdminAuth?.isSuperAdmin;

  return (
    <header
      className={`um-header bg-white shadow-sm border-bottom position-fixed w-100 top-0 ${
        sidebarCollapsed ? "header-collapsed" : "header-expanded"
      }`}
    >
      <div className="container-fluid px-2 px-sm-3 px-lg-4" style={{ maxWidth: "100%" }}>
        <div className="d-flex align-items-center justify-content-between py-2 gap-2" style={{ minHeight: "70px" }}>
          {/* Left Section - Page Title & Breadcrumb */}
          <div className="header-left flex-grow-1" style={{ minWidth: 0, overflow: "hidden" }}>
            <h4 className="mb-0 fw-bold text-dark text-truncate" style={{ fontSize: "1.15rem", lineHeight: 1.3 }}>
              {pageTitle}
            </h4>
            <nav aria-label="breadcrumb" className="d-none d-xl-block">
              <ol className="breadcrumb breadcrumb-modern mb-0" style={{ fontSize: "0.75rem" }}>
                <li className="breadcrumb-item text-muted">
                  <i className="fas fa-building me-1"></i>
                  {t("header.uniManager")}
                </li>
                <li className="breadcrumb-item active text-primary">
                  {pageTitle}
                </li>
              </ol>
            </nav>
          </div>

          {/* Right Section - Language Switcher & Logout & Back Button */}
          <div className="header-right d-flex align-items-center gap-2 flex-shrink-0">
            <LanguageSwitcher />

            {/* User Dropdown */}
            <div className="position-relative" ref={dropdownRef}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2 px-2.5 py-1"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                title={userEmail || t("header.logout", t("navigation:menu.logout", "Logout"))}
                style={{
                  fontSize: "0.75rem",
                  lineHeight: 1.2
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 24, height: 24, background: "linear-gradient(135deg, #28a745, #20c997)", color: "white", fontSize: "0.75rem" }}
                >
                  <i className="fas fa-user"></i>
                </div>
                {userEmail && (
                  <span className="small d-none d-md-inline fw-medium text-dark">{userEmail.split("@")[0]}</span>
                )}
                <i className="fas fa-chevron-down text-muted" style={{ fontSize: "0.65rem" }}></i>
              </button>

              {showUserDropdown && (
                <>
                  <div
                    className="dropdown-menu dropdown-menu-end show position-absolute mt-1 shadow border-0 rounded-3 p-2"
                    style={{ right: 0, minWidth: "210px", zIndex: 1000 }}
                  >
                    {userEmail && (
                      <div className="px-3 py-2 border-bottom mb-1">
                        <div className="fw-semibold text-dark text-truncate" style={{ fontSize: "0.85rem" }}>
                          {userEmail}
                        </div>
                        <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {isSuperAdmin ? "Super Admin" : "University Admin"}
                        </small>
                      </div>
                    )}
                    {userEmail && (
                      <button
                        type="button"
                        className="dropdown-item d-flex align-items-center gap-2 rounded py-2 px-3 fw-medium"
                        onClick={() => {
                          setShowUserDropdown(false);
                          setShowChangePasswordModal(true);
                        }}
                        style={{ fontSize: "0.85rem" }}
                      >
                        <i className="fas fa-key"></i>
                        <span>{t("header.changePassword")}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      className="dropdown-item d-flex align-items-center gap-2 text-danger rounded py-2 px-3 fw-medium"
                      onClick={() => {
                        setShowUserDropdown(false);
                        handleLogout();
                      }}
                      style={{ fontSize: "0.85rem" }}
                    >
                      <i className="fas fa-sign-out-alt"></i>
                      <span>{t("header.logout", t("navigation:menu.logout", "Logout"))}</span>
                    </button>
                  </div>
                </>
              )}
            </div>


          </div>
        </div>
      </div>
      <UMChangePasswordModal show={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} />
    </header>
  );
};

export default UniversityManagerHeader;
